'use client';

import React from 'react';

import { Heading } from '@depop/web-thrift-ui/Typography';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import clsx from 'clsx';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
} from '@/modules/activityTracker/constants.ts';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker.ts';
import { type ShopByStyleData } from '@/modules/cms/helpers/assertions/shopByStyle.ts';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { createCMSTileHref, getCMSImageForVariant } from '@/modules/home/utils';

type Props = {
  data: ShopByStyleData;
  runExperiment: boolean;
};

const TILE_WIDTH = 408;
const TILE_HEIGHT = 204;

export function ShopByStyleUi({ data, runExperiment }: Props) {
  const { sendActivityTrackerEvent } = useActivityTracker();
  const { web_7707_broaden_appeal_imagery } = useExperiments([
    'web_7707_broaden_appeal_imagery',
  ]);

  function sendATEvent(title: string, itemPosition: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.ShopByStyle,
      itemClicked: title,
      itemPosition,
      schemaVersion: '3.0',
    });
  }

  return (
    <section>
      <ModuleHeader title={data.titleCopy} size={ModuleHeaderSize.Large} />
      <div className={styles.tileContainer}>
        <ul className={styles.tileLayout}>
          {data.items.map((tile, index) => {
            const image = getCMSImageForVariant(
              tile,
              runExperiment ? web_7707_broaden_appeal_imagery : undefined
            );
            const srcSet = `${
              image?.url
            }?w=${TILE_WIDTH}&fm=webp ${TILE_WIDTH}w, ${image?.url}?w=${
              TILE_WIDTH * 1.5
            }&fm=webp ${TILE_WIDTH * 2}w`;

            return (
              <li key={tile.link}>
                <ExtendedLink
                  className={styles.link}
                  href={createCMSTileHref(
                    tile,
                    HomePageModuleNames.ShopByStyle
                  )}
                  onClick={() => sendATEvent(tile.title, index)}
                >
                  <div className={styles.imgOverlay}>
                    <LazyLoadImage
                      alt=""
                      srcSet={srcSet}
                      width={TILE_WIDTH}
                      height={TILE_HEIGHT}
                      src={`${image?.url}?w=${TILE_WIDTH * 2}&fm=webp`}
                      sizes="(max-width: 768px) 200px, (min-width: 768px) 400px"
                    />
                  </div>

                  <Heading variant="md" as="p" className={clsx(styles.title)}>
                    {tile.title}
                  </Heading>
                </ExtendedLink>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
